import { TrackerDataType } from '../constants/constants';
import { IssueKeys } from '../constants/IssueAndServiceKeys';
import { htmlToText } from 'html-to-text';
import dayjs from '../../utils/dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createInteraction, deleteInteraction, updateInteractionWithFiles } from '../requests/interactionsRequsts';
import { duplicateMediaInteraction } from '../../components/TrackerTable/trackerTableRequest';

export const useMediaInteractionHandler = ({ recentCreations, onSuccessCallback }) => {
  const queryClient = useQueryClient();
  const invalidateDropdownTable = async () => {
    await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
  };

  const { markCreated, isNewlyAdded } = recentCreations;

  const createInteractionMutation = useMutation({
    mutationFn: async ({ fields, files }) => {
      const payload = { ...fields };
      if (files?.length) {
        payload.file = files;
      }
      const data = await createInteraction(payload);
      return data;
    },
    onSuccess: async (_data) => {
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const duplicateInteractionMutation = useMutation({
    mutationFn: async (id) => {
      const data = await duplicateMediaInteraction(id);
      return data;
    },
    onSuccess: async ({ id }) => {
      if (id) {
        markCreated(id);
      }
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const updateInteractionWithFileMutation = useMutation({
    mutationFn: async ({ id, fields, uploadingUrls, removingUrls }) => {
      const data = await updateInteractionWithFiles({ id, fields, uploadingUrls, removingUrls });
      return data;
    },
    onSuccess: async () => {
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const deleteInteractionMutation = useMutation({
    mutationFn: async (id) => {
      const data = await deleteInteraction(id);
      return data;
    },
    onSuccess: async () => {
      await invalidateDropdownTable();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  return {
    type: TrackerDataType.MEDIA_INTERACTION,
    newItemModalTitle: 'Add a New Media Interaction',
    createNewItem: async (fields, files, successCallback) => {
      const result = await createInteractionMutation.mutateAsync({ fields, files });
      if (successCallback && result?.id) {
        markCreated(result.id);
        successCallback({ postId: result.id });
      }
    },
    useListenOnCreation: (rowId) => {
      return { isNewlyAdded: isNewlyAdded(rowId) };
    },
    duplicateItem: async (id) => {
      await duplicateInteractionMutation.mutateAsync(id);
    },
    editItem: (val, index) => {
      let newVal;
      if (!val) {
        newVal = null;
      } else {
        switch (index) {
          case IssueKeys.type:
            {
              let array = [];
              val.forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case IssueKeys.linkedService:
            newVal = val.filter((item) => item !== null);
            break;
          case IssueKeys.lead:
            {
              let array = [];
              val.forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case IssueKeys.outlet:
            newVal = val._id;
            //TODO cancel journalist when outlet changed
            break;
          case IssueKeys.journalist:
            newVal = val._id;
            break;
          case IssueKeys.topic:
          case IssueKeys.response:
          case IssueKeys.keyMessaging:
          case IssueKeys.entryDate:
          case IssueKeys.dueDate:
          case IssueKeys.date:
            newVal = val;
            break;
          case IssueKeys.expert:
            newVal = val;
            break;
          case IssueKeys.units:
          case IssueKeys.campaign:
            let arr = [];
            val.forEach((obj) => arr.push(obj));
            newVal = arr;
            break;
          case IssueKeys.file:
            newVal = val;
            break;
          case IssueKeys.dept:
            newVal = val.map((c) => c.name);
            break;
          default:
            newVal = val.name;
            break;
        }
      }
      return newVal;
    },
    updateItemWithFile: async ({ id, fields }, _uploadingFiles, uploadingUrls, _removingFiles, removingUrls) => {
      try {
        await updateInteractionWithFileMutation.mutateAsync({
          id,
          fields,
          uploadingUrls,
          removingUrls,
        });
      } catch (err) {
        console.error(`failed to update issue with files - ${err}`);
      }
    },
    deleteItem: async (id) => {
      try {
        const data = await deleteInteractionMutation.mutateAsync(id);
        return data;
      } catch (err) {
        console.error(`failed to delete issue - ${err}`);
      }
    },
    flattenForExport: (data, dropdownTable) => {
      return data.map((row) => {
        try {
          let flatRow = JSON.parse(JSON.stringify(row.cells));

          delete flatRow[IssueKeys.date];
          flatRow[IssueKeys.entryDate] = dayjs(flatRow[IssueKeys.entryDate]).format('YYYY-MM-DD');
          flatRow[IssueKeys.dueDate] = flatRow[IssueKeys.dueDate]
            ? dayjs(flatRow[IssueKeys.dueDate]).format('YYYY-MM-DD')
            : '';

          flatRow[IssueKeys.type] = flatRow[IssueKeys.type].map((c) => `${c.type}:${c.name}`).join(', \n');

          let obj;
          flatRow[IssueKeys.expert] = flatRow[IssueKeys.expert]
            .map((le) => dropdownTable['expert'].find((dropdownle) => dropdownle._id === le._id)?.name ?? '')
            .join(', \n');

          flatRow[IssueKeys.lead] = flatRow[IssueKeys.lead]
            .map((leadId) => dropdownTable['lead'].find((lead) => lead._id === leadId)?.name ?? null)
            .filter((leadName) => leadName !== null)
            .join(', ');

          flatRow[IssueKeys.linkedService] = flatRow[IssueKeys.linkedService].map((c) => c.name);

          obj = dropdownTable['journalist'].find((journalist) => journalist['_id'] === flatRow[IssueKeys.journalist]);
          flatRow[IssueKeys.journalist] = obj ? obj.name : null;
          flatRow['Journalist Email'] = obj ? obj.email : null;
          flatRow['Journalist Phone'] = obj ? obj.phone : null;

          obj = dropdownTable['outlet'].find((org) => org['_id'] === flatRow[IssueKeys.outlet]);
          flatRow[IssueKeys.outlet] = obj ? obj.name : null;

          flatRow[IssueKeys.file] = flatRow[IssueKeys.file].map((file) => {
            if (file.type === 'url') {
              return file.path;
            } else {
              return file.name;
            }
          });
          flatRow[IssueKeys.campaign] = flatRow[IssueKeys.campaign].map((campaign) => `${campaign.name}`).join(', \n');

          let messaging = flatRow[IssueKeys.keyMessaging] ? flatRow[IssueKeys.keyMessaging] : '';
          messaging += flatRow['Field Addons']?.keyMessaging ? flatRow['Field Addons'].keyMessaging : '';
          messaging = htmlToText(messaging, { wordwrap: null });
          messaging = messaging.replace(/"/g, '""');
          flatRow[IssueKeys.keyMessaging] = messaging;

          let response = flatRow[IssueKeys.response] ? flatRow[IssueKeys.response] : '';
          response += flatRow['Field Addons']?.response ? flatRow['Field Addons'].response : '';
          response = htmlToText(response, { wordwrap: null });
          response = response.replace(/"/g, '""');
          flatRow[IssueKeys.response] = response;

          delete flatRow[IssueKeys.autotracker];
          delete flatRow[IssueKeys.fieldAddons];

          return flatRow;
        } catch {
          return {
            [IssueKeys.entryDate]: '',
            [IssueKeys.dueDate]: '',
            [IssueKeys.contactMethod]: '',
            [IssueKeys.topic]: 'error flattening data for export!',
            [IssueKeys.type]: '',
            [IssueKeys.units]: '',
            [IssueKeys.expert]: '',
            [IssueKeys.outlet]: '',
            [IssueKeys.journalist]: '',
            'Journalist Email': '',
            'Journalist Phone': '',
            [IssueKeys.campaign]: '',
            [IssueKeys.lead]: '',
            [IssueKeys.response]: '',
            [IssueKeys.file]: '',
            [IssueKeys.status]: '',
            [IssueKeys.dept]: '',
            [IssueKeys.keyMessaging]: '',
            [IssueKeys.linkedService]: '',
          };
        }
      });
    },
  };
};
